import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  type Bar,
  barLayout,
  EMPTY_BAR,
  placeThumb,
  SEAM,
  SMALLEST_THUMB,
  scrollForThumb,
  sliverIndexAt,
  type ThumbPlacement,
} from './scrubberBar'
import { type AccountOf, rulerMarks } from './scrubberRuler'
import type { StripBand, StripLayout } from './strip'

const TRACK_HEIGHT = 28
// The line above the bar, with a tick and a number where each account starts. Names that would
// overlap go up a row, so the line is as tall as the rows it needs.
const ROW_HEIGHT = 12
const RULER_PAD = 4
const LABEL_RISE = 3
const TICK_ABOVE_LABEL = 8
const LABEL_LEAD = 3
const RULER_FONT = '10px "Geist Sans", sans-serif'
const RULER_LINE = '#d4d4d4'
const TICK_LINE = '#737373'
const TRACK_TINT = '#ffffff'
const ACTIVE_TINT = '#171717'
// Canvases are refused above a backing width of 32k in some browsers, so a long bar is drawn
// at a lower pixel ratio rather than not at all.
const WIDEST_CANVAS = 16384

export type SegmentTint = (statementId: string) => string

interface ScrubberProps {
  layout: StripLayout
  scrollWidth: number
  activeStatementId: string | null
  // The map's own verdict colour for a statement, so the bar reads in the map's language.
  tintFor: SegmentTint
  // The account a statement is filed under, for the marks above the bar.
  accountOf: AccountOf
  controls: string
  scrollRef: RefObject<HTMLElement | null>
  onGoToBand: (band: StripBand) => void
  onScrubTo: (scrollLeft: number) => void
  // A held thumb is still scrubbing even between two moves, so the strip waits for the release.
  onScrubStart: () => void
  onScrubEnd: () => void
}

interface Grip {
  x: number
  thumbLeft: number
}

const AT_REST: ThumbPlacement = { offset: 0, thumbLeft: 0, thumbWidth: 0 }

// The project as one bar, a sliver per band, never thinner than a floor that keeps each one visible.
// A long bar slides under the thumb like a minimap. The track is white because on the pane's grey
// the light slivers vanished and the dark ones looked broken.
export const Scrubber = ({
  layout,
  scrollWidth,
  activeStatementId,
  tintFor,
  accountOf,
  controls,
  scrollRef,
  onGoToBand,
  onScrubTo,
  onScrubStart,
  onScrubEnd,
}: ScrubberProps) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const trackWidth = useRef(0)
  const bar = useRef<Bar>(EMPTY_BAR)
  const placed = useRef<ThumbPlacement>(AT_REST)
  const grip = useRef<Grip | null>(null)
  const named = useRef<string | null>(null)

  // How many rows of names the line needs. Set from the paint, since only the brush can
  // measure a name, and it sizes the track and places the thumb.
  const [rows, setRows] = useState(1)
  const rulerHeight = rows * ROW_HEIGHT + RULER_PAD

  const paintRuler = useCallback(
    (brush: CanvasRenderingContext2D, width: number, height: number) => {
      brush.fillStyle = RULER_LINE
      brush.fillRect(0, height - 1, width, 1)
      const ink =
        getComputedStyle(trackRef.current as Element)
          .getPropertyValue('--color-ink-subtle')
          .trim() || TICK_LINE
      const ruler = rulerMarks(
        bar.current,
        accountOf,
        (text) => brush.measureText(text).width,
      )
      setRows(ruler.rows)
      for (const mark of ruler.marks) {
        const baseline = height - LABEL_RISE - mark.row * ROW_HEIGHT
        const tickTop = baseline - TICK_ABOVE_LABEL
        brush.fillStyle = TICK_LINE
        brush.fillRect(Math.round(mark.x), tickTop, 1, height - tickTop)
        if (mark.label) {
          brush.fillStyle = ink
          brush.fillText(mark.label, mark.x + LABEL_LEAD, baseline)
        }
      }
    },
    [accountOf],
  )

  const paint = useCallback(() => {
    const canvas = canvasRef.current
    const { slivers, width } = bar.current
    if (!canvas || width === 0) return
    const height = rulerHeight + TRACK_HEIGHT
    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      WIDEST_CANVAS / width,
    )
    canvas.width = Math.round(width * pixelRatio)
    canvas.height = Math.round(height * pixelRatio)
    canvas.style.width = `${width}px`
    const brush = canvas.getContext('2d')
    if (!brush) return

    brush.scale(pixelRatio, pixelRatio)
    brush.clearRect(0, 0, width, height)
    brush.font = RULER_FONT
    paintRuler(brush, width, rulerHeight)
    brush.fillStyle = TRACK_TINT
    brush.fillRect(0, rulerHeight, width, TRACK_HEIGHT)
    for (const sliver of slivers) {
      const active = sliver.statementId === activeStatementId
      brush.fillStyle = active ? ACTIVE_TINT : tintFor(sliver.statementId)
      // The open statement takes its seam too, so it reads a touch bolder than its neighbours.
      brush.fillRect(
        sliver.left,
        rulerHeight,
        active ? sliver.width : sliver.width - SEAM,
        TRACK_HEIGHT,
      )
    }
  }, [activeStatementId, paintRuler, rulerHeight, tintFor])

  // The thumb is the viewport, and the bar slides under it. Both move by writing a transform,
  // never by re-rendering.
  const followScroll = useCallback(() => {
    const element = scrollRef.current
    const thumb = thumbRef.current
    const canvas = canvasRef.current
    const width = trackWidth.current
    if (!element || !thumb || !canvas || width === 0 || scrollWidth === 0)
      return
    const placement = placeThumb(
      bar.current,
      width,
      element.scrollLeft,
      element.clientWidth,
      scrollWidth,
    )
    placed.current = placement
    canvas.style.transform = `translateX(${-placement.offset}px)`
    thumb.style.width = `${placement.thumbWidth}px`
    thumb.style.transform = `translateX(${placement.thumbLeft}px)`
  }, [scrollRef, scrollWidth])

  const redraw = useCallback(() => {
    trackWidth.current = trackRef.current?.clientWidth ?? 0
    bar.current = barLayout(layout, scrollWidth, trackWidth.current)
    paint()
    followScroll()
  }, [followScroll, layout, paint, scrollWidth])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const observer = new ResizeObserver(redraw)
    observer.observe(track)
    redraw()
    return () => observer.disconnect()
  }, [redraw])

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        followScroll()
      })
    }
    element.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      element.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [followScroll, scrollRef])

  // The band drawn under this point of the track, bar slide included.
  const bandUnder = (clientX: number): StripBand | null => {
    const track = trackRef.current
    if (!track || bar.current.width === 0) return null
    const frame = track.getBoundingClientRect()
    const barX = clientX - frame.left + placed.current.offset
    return layout.bands[sliverIndexAt(bar.current, barX)] ?? null
  }

  const jumpToBand = (band: StripBand | null) => {
    if (band) onGoToBand(band)
  }

  const walkBands = (step: number) => {
    const at = layout.bands.findIndex(
      (band) => band.statementId === activeStatementId,
    )
    const wanted = Math.min(
      Math.max(at + step, 0),
      Math.max(layout.bands.length - 1, 0),
    )
    jumpToBand(layout.bands[wanted] ?? null)
  }

  // The scrubber is not the pages, so a press here must not start a sweep over them. A press on
  // the thumb drags it; a press anywhere else goes to the sliver under the pointer.
  const press = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.stopPropagation()
    const track = trackRef.current
    const element = scrollRef.current
    if (!track || !element || event.button !== 0) return

    if (thumbRef.current?.contains(event.target as Node)) {
      grip.current = { x: event.clientX, thumbLeft: placed.current.thumbLeft }
      onScrubStart()
      track.setPointerCapture(event.pointerId)
      return
    }
    jumpToBand(bandUnder(event.clientX))
  }

  // A drag moves the thumb with the pointer and lets the bar drift underneath. Steering by the
  // sliver under the pointer instead would move the target as the bar slid, and run away.
  const dragOrName = (event: ReactPointerEvent<HTMLDivElement>) => {
    const track = trackRef.current
    const element = scrollRef.current
    if (!track || !element) return

    const held = grip.current
    if (held) {
      onScrubTo(
        scrollForThumb(
          bar.current,
          track.clientWidth,
          held.thumbLeft + (event.clientX - held.x),
          element.clientWidth,
          scrollWidth,
        ),
      )
      return
    }

    const band = bandUnder(event.clientX)
    if (!band || band.statementId === named.current) return
    named.current = band.statementId
    track.title = band.label
  }

  const letGoOfThumb = () => {
    if (!grip.current) return
    grip.current = null
    onScrubEnd()
  }

  const activeIndex = Math.max(
    layout.bands.findIndex((band) => band.statementId === activeStatementId),
    0,
  )

  return (
    <div
      ref={trackRef}
      data-scrubber
      role="scrollbar"
      tabIndex={0}
      aria-controls={controls}
      aria-orientation="horizontal"
      aria-label="The whole project"
      aria-valuemin={0}
      aria-valuemax={Math.max(layout.bands.length - 1, 0)}
      aria-valuenow={activeIndex}
      aria-valuetext={layout.bands[activeIndex]?.label ?? 'No statements'}
      onPointerDown={press}
      onPointerMove={dragOrName}
      onPointerUp={letGoOfThumb}
      onPointerCancel={letGoOfThumb}
      onKeyDown={(event) => {
        const steps: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1 }
        if (event.key in steps) {
          event.preventDefault()
          walkBands(steps[event.key])
          return
        }
        if (event.key === 'Home') jumpToBand(layout.bands[0] ?? null)
        if (event.key === 'End') {
          jumpToBand(layout.bands[layout.bands.length - 1] ?? null)
        }
      }}
      style={{ height: rulerHeight + TRACK_HEIGHT }}
      className="relative w-full shrink-0 cursor-pointer select-none overflow-hidden focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-neutral-900"
    >
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 block will-change-transform"
        style={{ height: rulerHeight + TRACK_HEIGHT }}
      />
      <div
        ref={thumbRef}
        className="absolute left-0 cursor-grab rounded-sm outline-2 -outline-offset-2 outline-neutral-900 will-change-transform active:cursor-grabbing"
        style={{
          top: rulerHeight,
          height: TRACK_HEIGHT,
          width: SMALLEST_THUMB,
        }}
      />
    </div>
  )
}
